import {
  getNextOccurrence,
  getScheduleStatus,
  validateWeeklySchedule,
  WeeklySchedule,
} from "./raffleSchedule"

export const FALLBACK_TIME_ZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/Bogota",
  "America/Santo_Domingo",
  "America/Argentina/Buenos_Aires",
  "Europe/Madrid",
  "Europe/London",
]

export const listTimeZones = (current?: string): string[] => {
  let zones: string[] = []
  try {
    const supported = (Intl as any).supportedValuesOf?.("timeZone")
    zones = Array.isArray(supported) && supported.length ? [...supported] : []
  } catch {
    zones = []
  }
  if (!zones.length) zones = [...FALLBACK_TIME_ZONES]
  if (!zones.includes("UTC")) zones.unshift("UTC")
  if (current && !zones.includes(current)) zones.unshift(current)
  return zones
}

export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

const pad = (n: number) => String(n).padStart(2, "0")
export const todayDateKey = (now: number = Date.now()) => {
  const d = new Date(now)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Defaults a freshly enabled schedule so the admin only has to pick the weekdays.
export const withScheduleDefaults = (
  schedule: WeeklySchedule | undefined,
  now: number = Date.now(),
): WeeklySchedule => ({
  enabled: false,
  paused: false,
  weekdays: [],
  publishTime: "09:00",
  endTime: "18:00",
  timezone: getBrowserTimeZone(),
  startDate: todayDateKey(now),
  repeatIndefinitely: true,
  ...(schedule || {}),
})

export const formatInZone = (utcMs: number, timeZone: string, locale?: string) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(utcMs))
  } catch {
    return new Date(utcMs).toISOString()
  }
}

export const buildWeeklyScheduleViewModel = (
  schedule: WeeklySchedule | undefined,
  now: number = Date.now(),
) => {
  const value = withScheduleDefaults(schedule, now)
  const enabled = value.enabled === true
  const errors = enabled ? validateWeeklySchedule(value) : []
  const next = enabled && errors.length === 0 ? getNextOccurrence(value, now) : null
  const zone = value.timezone as string
  return {
    value,
    errors,
    status: getScheduleStatus(value, now),
    nextPublishLabel: next ? formatInZone(next.publishAt, zone) : null,
    nextEndLabel: next ? formatInZone(next.endAt, zone) : null,
  }
}
